import {
  Button,
  ButtonGroup,
  Checkbox,
  Popover,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { FilterTag } from "./FilterTag";
import { TreatmentType } from "@keita/lanepatrol-sdk";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { AddFilterButton } from "./AddFilterButton";

const TreatmentFiltersBase: React.FC = () => {
  const { filterStore } = useStores();
  const { open, onToggle, onClose } = useDisclosure();
  const [selectedItems, setSelectedItems] = useState(new Set<TreatmentType>());

  useEffect(() => {
    if (open) {
      setSelectedItems(() => new Set(filterStore.treatmentFilters));
    }
    //eslint-disable-next-line
  }, [open]);

  const handleClear = () => {
    setSelectedItems(new Set());
  };

  const handleSubmit = () => {
    filterStore.setTreatmentFilters([...selectedItems]);
    onClose();
  };

  const removeActiveFilter = (type: TreatmentType) => {
    filterStore.setTreatmentFilters(
      filterStore.treatmentFilters.filter((item) => item !== type),
    );
  };

  return (
    <>
      <CustomPopover
        open={open}
        onClose={onClose}
        asChild={false}
        trigger={
          <AddFilterButton label="Safety suggestion" onClick={onToggle} />
        }
        minW="620px"
        maxH="360px"
      >
        <Popover.Header fontWeight={"semibold"}>
          {t("map:filters.byTreatmentTitle")}
        </Popover.Header>
        <Popover.Body overflowY="auto">
          <VStack alignItems="start">
            {filterStore.treatmentsAvailableInCurrentMode.map(
              (treatment, i) => (
                <Checkbox.Root
                  key={i}
                  colorPalette="primary"
                  name={treatment}
                  checked={selectedItems.has(treatment)}
                  onCheckedChange={({ checked }) => {
                    setSelectedItems((prev) => {
                      if (checked) {
                        prev.add(treatment);
                      } else {
                        prev.delete(treatment);
                      }
                      return new Set(prev);
                    });
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>
                    <Text fontSize="sm">
                      {t(`sdk:treatments.${treatment}`)}
                    </Text>
                  </Checkbox.Label>
                </Checkbox.Root>
              ),
            )}
          </VStack>
        </Popover.Body>
        <Popover.Footer display="flex" justifyContent="flex-end">
          <ButtonGroup colorPalette="primary" size="sm">
            <Button onClick={handleClear} disabled={!selectedItems.size}>
              {t("map:filters.clearButton")}
            </Button>
            <Button onClick={handleSubmit}>
              {t("map:filters.submitButton")}
            </Button>
          </ButtonGroup>
        </Popover.Footer>
      </CustomPopover>

      {!!filterStore.treatmentFilters.length &&
        filterStore.treatmentFilters.map((treatmentType, i) => (
          <FilterTag key={i} onClose={() => removeActiveFilter(treatmentType)}>
            {t(`sdk:treatments.${treatmentType}`)}
          </FilterTag>
        ))}
    </>
  );
};

export const TreatmentFilters = observer(TreatmentFiltersBase);
